// src/modules/leads/leadEstimateServiceFilter.js
//
// Shared helper for filtering leads by the services on their ACCEPTED quotes.
// The lead objects loaded by the map views don't carry estimate line-item data,
// so we fetch the accepted estimates separately and build a leadId -> service-names
// lookup the map views can filter against.
import { getEstimatesByStatus } from '../estimates/EstimateService';

// Build a map of leadId -> Set(service_name) plus a sorted, deduped list of all
// service names appearing on the given estimates. `estimates` is the array of
// Accepted estimates, each with `leadId` and `items[].Service.service_name`.
export const buildAcceptedServiceMap = (estimates = []) => {
  const serviceMap = new Map();
  const allNames = new Set();

  estimates.forEach((est) => {
    if (est?.leadId == null) return;
    const names = serviceMap.get(est.leadId) || new Set();
    (est.items || []).forEach((item) => {
      const name = item?.Service?.service_name;
      if (name) {
        names.add(name);
        allNames.add(name);
      }
    });
    serviceMap.set(est.leadId, names);
  });

  const availableServices = Array.from(allNames).sort((a, b) => a.localeCompare(b));
  return { serviceMap, availableServices };
};

// Service names the lead asked for (its service requests), regardless of quote status.
export const getRequestedServiceNames = (lead) =>
  (lead?.serviceRequests || [])
    .map((sr) => sr?.Service?.service_name)
    .filter(Boolean);

// Returns true when a lead passes the service filter. `serviceFilter` is an array
// of selected service names ([] = no filter / all). ANY-match semantics: the lead
// matches if a selected service is on its Accepted quote OR was requested on the
// lead itself (so prospects with no accepted quote can be filtered too).
export const leadMatchesServiceFilter = (lead, serviceFilter, serviceMap) => {
  if (!serviceFilter || serviceFilter.length === 0) return true;
  const accepted = serviceMap?.get(lead.id);
  return serviceFilter.some(
    (n) => accepted?.has(n) || getRequestedServiceNames(lead).includes(n)
  );
};

// Fetch accepted estimates and build the lookup. Falls back to empty data on error
// so a failed fetch never breaks the map view (the filter simply has no options).
export const fetchAcceptedServiceMap = async () => {
  try {
    const res = await getEstimatesByStatus('Accepted');
    const data = res?.data;
    const estimates = Array.isArray(data) ? data : (data?.estimates ?? []);
    return buildAcceptedServiceMap(estimates);
  } catch (error) {
    console.error('Failed to load accepted-estimate services:', error);
    return { serviceMap: new Map(), availableServices: [] };
  }
};
